import { Check } from "lucide-react";
import { ApiError, type ResteARegler } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { formatDateShort, formatXof } from "@/lib/format";

/** Montant minimal d'un paiement en ligne (contrôle du serveur : 100 F). */
export const MONTANT_MIN_EN_LIGNE = 100;

/** Ce que le parent peut régler en ligne : une tranche, ou le reste du motif. */
export interface OptionReste {
  /** Tranche réglée ; `null` : reste du motif. */
  echeanceId: string | null;
  libelle: string;
  dateEcheance: string | null;
  /** Reste exact, non modifiable : c'est le montant payé. */
  montant: number;
}

/** Clé stable d'une option (la tranche, ou « motif »). */
export function cleOption(o: OptionReste): string {
  return o.echeanceId ?? "motif";
}

/**
 * Options proposées (décision du PO du 21/10/2026) : chaque tranche non
 * soldée avec son reste exact, puis le reste du motif. Une seule tranche qui
 * porte tout le reste ne donne qu'une option : la régler, c'est régler le
 * motif. Motif soldé : aucune option.
 */
export function optionsDeReste(r: ResteARegler): OptionReste[] {
  if (r.resteXof <= 0) return [];
  const tranches: OptionReste[] = r.tranches.map((t) => ({
    echeanceId: t.echeanceId,
    libelle: t.libelle,
    dateEcheance: t.dateEcheance ?? null,
    montant: t.resteXof,
  }));
  if (tranches.length === 1 && tranches[0].montant === r.resteXof) return tranches;
  return [
    ...tranches,
    {
      echeanceId: null,
      libelle: tranches.length > 0 ? "Tout le reste du motif" : "Reste du motif",
      dateEcheance: null,
      montant: r.resteXof,
    },
  ];
}

/**
 * Option retenue : celle que le parent a touchée si elle existe encore et se
 * règle en ligne, sinon la première qui se règle en ligne (la tranche la plus
 * ancienne de l'échéancier).
 */
export function optionChoisie(options: OptionReste[], cle: string | null): OptionReste | null {
  const enLigne = options.filter((o) => o.montant >= MONTANT_MIN_EN_LIGNE);
  return enLigne.find((o) => cleOption(o) === cle) ?? enLigne[0] ?? null;
}

interface Props {
  /** Matricule retenu (élève choisi ou saisi) ; vide : rien à lire. */
  matricule: string;
  reste: ResteARegler | undefined;
  error: unknown;
  choix: OptionReste | null;
  onChoisir: (o: OptionReste) => void;
  onReessayer: () => void;
}

function messageErreur(err: unknown): string {
  if (err instanceof ApiError && err.status === 429) {
    return "Trop de recherches depuis cette connexion. Réessayez dans quelques minutes.";
  }
  if (err instanceof ApiError && err.status === 404) {
    return "Aucun élève de cette école pour ce matricule. Vérifiez-le.";
  }
  return friendlyError(err);
}

/**
 * Liste des tranches non soldées et de leur reste, après le choix de l'élève
 * et du motif. Le parent choisit une tranche (montant = son reste exact) ou le
 * reste du motif ; aucun montant libre. Ne montre que ce qu'il faut pour payer :
 * libellé, échéance, reste.
 */
export function ResteAReglerPicker({ matricule, reste, error, choix, onChoisir, onReessayer }: Props) {
  if (!matricule) {
    return (
      <p className="text-sm text-navy/60">
        Indiquez le matricule de votre enfant : le montant à régler en dépend.
      </p>
    );
  }
  if (error) {
    return (
      <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <p>{messageErreur(error)}</p>
        <button type="button" onClick={onReessayer} className="mt-2 text-xs font-bold text-amber-700 underline">
          Réessayer
        </button>
      </div>
    );
  }
  if (!reste) {
    return <p className="text-sm text-navy/60">Calcul du reste à régler…</p>;
  }
  const options = optionsDeReste(reste);
  if (options.length === 0) {
    return (
      <p className="text-sm font-medium text-[#3F8F5C]">
        Ce motif est déjà soldé pour cet enfant : il n'y a plus rien à payer.
      </p>
    );
  }
  return (
    <div>
      <div role="radiogroup" aria-label="Ce que vous réglez" className="grid gap-2">
        {options.map((o) => {
          const enLigne = o.montant >= MONTANT_MIN_EN_LIGNE;
          const actif = choix != null && cleOption(choix) === cleOption(o);
          return (
            <button
              key={cleOption(o)}
              type="button"
              role="radio"
              aria-checked={actif}
              disabled={!enLigne}
              data-testid={`reste-${cleOption(o)}`}
              onClick={() => onChoisir(o)}
              className={`flex items-center justify-between gap-3 rounded-[14px] border p-4 text-left transition-colors ${
                !enLigne
                  ? "cursor-not-allowed border-[#E5E5E5] bg-cream/60 opacity-70"
                  : actif
                    ? "border-navy bg-navy/5 ring-1 ring-navy"
                    : "border-[#E5E5E5] bg-white hover:border-navy/30"
              }`}
            >
              <div className="min-w-0">
                <div className="font-bold text-navy">{o.libelle}</div>
                <div className="text-xs text-navy/60">
                  {o.dateEcheance ? `Échéance : ${formatDateShort(o.dateEcheance)}` : "Reste à régler"}
                  {enLigne ? "" : " · moins de 100 F, à régler au guichet de l'école"}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="[font-family:'Cormorant_Garamond',serif] text-[20px] font-bold leading-none text-navy">
                  {formatXof(o.montant)}
                </span>
                {actif && (
                  <span aria-hidden className="flex h-5 w-5 items-center justify-center rounded-full bg-navy text-white">
                    <Check size={12} />
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-navy/50">
        Le montant est le reste exact : il ne se modifie pas.
      </p>
    </div>
  );
}
